/**
 * The end-of-day awards (decision 33): eight cards the wall hands out before the podium. All of
 * them are worked out from the log, so a corrected score moves them by itself. Their fun points
 * only count once the awards are revealed (state.reveal.awards), so nobody sees the fun board
 * jump about during the day.
 */

import { benchPoints } from "./bench";
import { mainBoard } from "./ranking";
import { gameResult } from "./scoring";
import { displayName } from "./selectors";
import type { PlayerId, TournamentState, Where } from "./types";

export interface Award {
  id: "picker" | "cursed" | "giant" | "heckler" | "upset" | "closest" | "iron" | "thrashing";
  title: string;
  /** One line, names included. */
  detail: string;
  winners: PlayerId[];
  /** Fun points to each winner. */
  points: number;
}

/** What each award is worth to each of its winners. */
export const AWARD_POINTS: Record<Award["id"], number> = {
  picker: 5,
  cursed: 3,
  giant: 3,
  heckler: 3,
  upset: 3,
  closest: 2,
  iron: 3,
  thrashing: 3,
};

/** The awards that have a winner so far, in the order the wall shows them. */
export function awards(state: TournamentState, where: Where = "wall"): Award[] {
  const name = (id: PlayerId): string => (state.players[id] ? displayName(state.players[id], where) : id);
  const team = (p: readonly PlayerId[]): string => p.map(name).join(" & ");
  const list = (ids: PlayerId[]): string => ids.map(name).join(", ");
  const byName = (x: PlayerId, y: PlayerId): number => name(x).localeCompare(name(y), "en");
  const here = Object.keys(state.players).sort(byName);
  const out: Award[] = [];
  const give = (id: Award["id"], title: string, winners: PlayerId[], detail: string): void => {
    out.push({ id, title, detail, winners, points: AWARD_POINTS[id] });
  };
  /** Everyone level on the highest count, as long as it is at least 1. */
  const most = (count: (id: PlayerId) => number): { ids: PlayerId[]; n: number } => {
    const n = Math.max(0, ...here.map(count));
    return { ids: n > 0 ? here.filter((id) => count(id) === n) : [], n };
  };

  const bench = benchPoints(state);

  // the scores themselves
  interface Played { roundNo: number; court: number; winners: PlayerId[]; losers: PlayerId[]; hi: number; lo: number }
  const played: Played[] = [];
  const gamesOf = new Map<PlayerId, { games: number; points: number }>();
  for (const round of state.rounds) {
    for (const game of round.games) {
      const result = gameResult(game, state.config);
      if (!result) continue;
      const [w, l] = result.a.won ? [result.a, result.b] : [result.b, result.a];
      played.push({ roundNo: round.no, court: game.court, winners: [...w.players], losers: [...l.players], hi: w.raw, lo: l.raw });
      for (const id of [...w.players, ...l.players]) {
        const row = gamesOf.get(id) ?? { games: 0, points: 0 };
        row.games += 1;
        row.points += w.raw + l.raw;
        gamesOf.set(id, row);
      }
    }
  }
  const line = (g: Played): string => `${team(g.winners)} ${g.hi}-${g.lo} ${team(g.losers)}, round ${g.roundNo}`;
  /** Every game level with the first after sorting (same order, same score): RULES, everyone level shares it. */
  const level = (sorted: Played[], order: (x: Played, y: Played) => number): Played[] => sorted.filter((g) => order(g, sorted[0]!) === 0);
  /** The players of some games, each once, in the order they come. */
  const each = (ids: PlayerId[]): PlayerId[] => [...new Set(ids)];
  /** One game names its players; several level games say the score and where they were. */
  const lines = (games: Played[]): string => {
    if (games.length === 1) return line(games[0]!);
    const g = games[0]!;
    const rounds = [...new Set(games.map((x) => x.roundNo))];
    const where = rounds.length === 1 ? `round ${rounds[0]}, courts ${and(games.map((x) => x.court))}` : `rounds ${and(rounds)}`;
    return `${count(games.length)} games at ${g.hi}-${g.lo}, ${where}`;
  };

  const picker = most((id) => bench.get(id)?.right ?? 0);
  if (picker.ids.length) {
    // level on right picks: the longer streak takes it
    const best = Math.max(...picker.ids.map((id) => bench.get(id)?.bestStreak ?? 0));
    const ids = picker.ids.filter((id) => (bench.get(id)?.bestStreak ?? 0) === best);
    give("picker", "Best picker", ids, `${list(ids)}: ${picker.n} right pick${picker.n === 1 ? "" : "s"}`);
  }

  const cursedCount = new Map<PlayerId, number>();
  for (const curse of state.bench.curses ?? []) {
    const game = state.rounds[curse.roundNo - 1]?.games.find((g) => g.court === curse.court);
    for (const id of game ? game[curse.side] : []) cursedCount.set(id, (cursedCount.get(id) ?? 0) + 1);
  }
  const cursed = most((id) => cursedCount.get(id) ?? 0);
  if (cursed.ids.length) give("cursed", "Most cursed", cursed.ids, `${list(cursed.ids)}: cursed ${cursed.n} time${cursed.n === 1 ? "" : "s"}`);

  const giant = most((id) => bench.get(id)?.bountiesBeaten ?? 0);
  if (giant.ids.length) give("giant", "Giant killer", giant.ids, `${list(giant.ids)}: beat the top three ${giant.n} time${giant.n === 1 ? "" : "s"}`);

  const heckler = most((id) => state.players[id]?.heckles ?? 0);
  if (heckler.ids.length) give("heckler", "Loudest heckler", heckler.ids, `${list(heckler.ids)}: ${heckler.n} heckle${heckler.n === 1 ? "" : "s"}`);

  // biggest upset: the winners furthest below the losers on the board as it stood before the round;
  // games with the same gap all share it
  let upsets: { game: Played; gap: number; text: string }[] = [];
  for (const round of state.rounds) {
    const games = played.filter((g) => g.roundNo === round.no);
    if (round.no < 2 || !games.length) continue;
    const before = mainBoard({ ...state, rounds: state.rounds.slice(0, round.no - 1) });
    const rank = new Map(before.filter((r) => r.games > 0).map((r) => [r.id, r.rank]));
    for (const game of games) {
      const w = game.winners.map((id) => rank.get(id));
      const l = game.losers.map((id) => rank.get(id));
      if ([...w, ...l].some((r) => r === undefined)) continue;
      const gap = (w[0]! + w[1]!) / 2 - (l[0]! + l[1]!) / 2;
      if (gap <= 0 || (upsets.length && gap < upsets[0]!.gap)) continue;
      const text = `${team(game.winners)} (ranked ${w[0]} and ${w[1]}) beat ${team(game.losers)} (${l[0]} and ${l[1]}), round ${game.roundNo}`;
      if (upsets.length && gap > upsets[0]!.gap) upsets = [];
      upsets.push({ game, gap, text });
    }
  }
  if (upsets.length === 1) give("upset", "Biggest upset", upsets[0]!.game.winners, upsets[0]!.text);
  else if (upsets.length) {
    const pairs = [...new Set(upsets.map((u) => team(u.game.winners)))];
    const places = Math.round(upsets[0]!.gap);
    const below = `about ${places} place${places === 1 ? "" : "s"} below the pair they beat`;
    const text = pairs.length === 1 ? `${pairs[0]}, ${times(upsets.length)}, each time ${below}` : `${and(pairs)}, each ${below}`;
    give("upset", "Biggest upset", each(upsets.flatMap((u) => u.game.winners)), text);
  }

  if (played.length) {
    const tightest = (x: Played, y: Played): number => x.hi - x.lo - (y.hi - y.lo) || y.hi + y.lo - (x.hi + x.lo);
    const closest = level([...played].sort(tightest), tightest);
    give("closest", "Closest game", each(closest.flatMap((g) => [...g.winners, ...g.losers])), lines(closest));

    // iron legs: the most games, then the most points played through
    const iron = most((id) => gamesOf.get(id)?.games ?? 0);
    const through = Math.max(...iron.ids.map((id) => gamesOf.get(id)?.points ?? 0));
    const legs = iron.ids.filter((id) => (gamesOf.get(id)?.points ?? 0) === through);
    give("iron", "Iron legs", legs, `${list(legs)}: ${iron.n} game${iron.n === 1 ? "" : "s"}, ${through} points played`);

    const widest = (x: Played, y: Played): number => y.hi - y.lo - (x.hi - x.lo) || x.lo - y.lo;
    const thrashing = level([...played].sort(widest), widest);
    // when every game finished the same way the two would be the same games: only Closest game
    const same = thrashing.length === closest.length && thrashing.every((g) => closest.includes(g));
    if (!same) give("thrashing", "Biggest thrashing", each(thrashing.flatMap((g) => g.winners)), lines(thrashing));
  }

  return out;
}

const COUNTS = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten"];

/** "Two", "Three": a count at the start of a line. */
function count(n: number): string {
  return COUNTS[n] ?? String(n);
}

/** "twice", "3 times". */
function times(n: number): string {
  return n === 2 ? "twice" : `${n} times`;
}

/** "7 and 9", "3, 7 and 9". */
function and(items: (string | number)[]): string {
  const all = items.map(String);
  return all.length < 2 ? all.join("") : `${all.slice(0, -1).join(", ")} and ${all.at(-1)}`;
}

/** The fun points the awards are worth, per player. */
export function awardPoints(state: TournamentState): Map<PlayerId, number> {
  const out = new Map<PlayerId, number>();
  for (const award of awards(state)) {
    for (const id of award.winners) out.set(id, (out.get(id) ?? 0) + award.points);
  }
  return out;
}
